<template>
    <div v-show="shouldShowGroup" class="playbook-group" :data-group-id="group.id">
        <div class="playbook-group__header"
            @click="toggleExpanded"
            @keydown.enter="toggleExpanded"
            @keydown.space.prevent="toggleExpanded"
            role="button"
            tabindex="0"
            :aria-expanded="isExpanded.toString()"
        >
            <h2 class="playbook-group__title">{{ group.name.toUpperCase() }}</h2>
            <span class="playbook-group__count">({{ visiblePlaybooks.length }} {{ visiblePlaybooks.length === 1 ? 'playbook' : 'playbooks' }})</span>
            <button
                type="button"
                class="playbook-group__toggle"
                :class="{ 'is-expanded': isExpanded }"
                :aria-label="`Toggle ${group.name} playbooks`"
                tabindex="-1"
            >
                <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                    <path d="M8.75 13.125L17.5 21.875L26.25 13.125" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <div class="playbook-group__separator"></div>
        </div>
        <transition name="expand" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
            <div class="playbook-group__content" :aria-hidden="!isExpanded" v-show="isExpanded">
                <div v-if="visiblePlaybooks.length > 0" class="playbook-grid">
                    <playbook-card
                        v-for="playbook in visiblePlaybooks"
                        :key="playbook.id"
                        :playbook="playbook"
                    />
                </div>
                <p v-else class="playbook-group__empty">No playbooks match your selected filters.</p>
            </div>
        </transition>
    </div>
</template>

<script>
import PlaybookCard from './PlaybookCard.vue';

export default {
    name: 'PlaybookGroup',
    components: {
        PlaybookCard,
    },
    props: {
        group: {
            type: Object,
            required: true,
            default: () => ({
                id: null,
                name: '',
                filterable: true,
                dynamic: false,
                playbooks: [],
            }),
        },
        selectedFilters: {
            type: Object,
            default: () => ({
                years: [],
                markets: [],
                topics: [],
            }),
        },
        initiallyExpanded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isExpanded: this.initiallyExpanded,
        };
    },
    mounted() {
        this.resetExpanded();
    },
    computed: {
        hasActiveFilters() {
            return (
                this.selectedFilters.years.length > 0 ||
                this.selectedFilters.markets.length > 0 ||
                this.selectedFilters.topics.length > 0
            );
        },
        hasVisiblePlaybooks() {
            return this.visiblePlaybooks.length > 0;
        },
        visiblePlaybooks() {
            if (!this.group.filterable || !this.hasActiveFilters || !this.group.playbooks) {
                return this.group.playbooks || [];
            }

            return this.group.playbooks.filter((playbook) => {
                // Check year filter
                if (this.selectedFilters.years.length > 0) {
                    const playbookYear = new Date(playbook.date_timestamp * 1000).getFullYear();
                    if (!this.selectedFilters.years.includes(playbookYear)) {
                        return false;
                    }
                }

                // Check market filter
                if (this.selectedFilters.markets.length > 0) {
                    if (!this.selectedFilters.markets.includes(playbook.market_id)) {
                        return false;
                    }
                }

                // Check topic filter
                if (this.selectedFilters.topics.length > 0) {
                    if (!this.selectedFilters.topics.includes(playbook.topic_id)) {
                        return false;
                    }
                }

                return true;
            });
        },
        shouldShowGroup() {
            if (!this.group.filterable || !this.hasActiveFilters) {
                return true;
            }

            // Only show year filter check for group visibility
            if (this.selectedFilters.years.length > 0) {
                if (!this.selectedFilters.years.includes(parseInt(this.group.id))) {
                    return false;
                }
            }

            return this.visiblePlaybooks.length > 0;
        },
    },
    methods: {
        toggleExpanded() {
            this.isExpanded = !this.isExpanded;
        },
        setExpanded(expanded) {
            this.isExpanded = expanded;
        },
        onEnter(el) {
            el.style.height = '0';
            el.style.overflow = 'hidden';
            el.offsetHeight;
            el.style.transition = 'height 0.3s ease-out';
            el.style.height = el.scrollHeight + 'px';
        },
        onAfterEnter(el) {
            el.style.height = '';
            el.style.overflow = '';
            el.style.transition = '';
        },
        onLeave(el) {
            el.style.height = el.scrollHeight + 'px';
            el.style.overflow = 'hidden';
            el.offsetHeight;
            el.style.transition = 'height 0.3s ease-out';
            el.style.height = '0';
        },
        resetExpanded() {
            if (!this.hasActiveFilters) {
                this.isExpanded = this.initiallyExpanded;
            } else {
                this.isExpanded = this.hasVisiblePlaybooks;
            }
        },
    },
};
</script>

<style scoped>
.playbook-group {
    margin-bottom: 40px;
}

.playbook-group__header {
    position: relative;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding-bottom: 15px;
}

.playbook-group__title {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    font-size: 27px;
    color: black;
    text-transform: uppercase;
    margin: 0;
}

.playbook-group__count {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: #666;
    margin-left: 10px;
    flex: 1;
}

.playbook-group__toggle {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: transform 0.3s ease;
    color: #999;
}

.playbook-group__toggle.is-expanded {
    transform: rotate(180deg);
}

.playbook-group__separator {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(to right, #ddd 0%, transparent 100%);
}

.playbook-group__content {
    padding-top: 30px;
}

.playbook-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    width: 100%;
}

.playbook-group__empty {
    color: #666;
    font-style: italic;
    text-align: center;
    padding: 40px;
}

@media (max-width: 768px) {
    .playbook-group__title {
        font-size: 24px;
    }

    .playbook-grid {
        gap: 20px;
    }
}
</style>
